/**
 * @file     usePreferenceSync.ts
 * @purpose  Debounced hook that syncs filter/preference changes to
 *           user_property_filter_profile DB for authenticated users.
 *           Safe to call on every filter change — debounced 1.2s so DB
 *           writes happen after the user stops adjusting.
 *           Also writes deal_finder_* keys to visitor profile localStorage
 *           so anonymous browsing state is preserved for later sign-up seeding.
 */
'use client';

import { useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { secureFetch } from '@/services/anti-scraping/secureFetch';

export interface PreferencePatch {
  regions?: string[];
  propertyTypes?: string[];
  bedsMin?: number | null;
  bathsMin?: number | null;
  priceMin?: number | null;
  priceMax?: number | null;
  livingAreaMin?: number | null;
  parkingMin?: number | null;
  openHouseOnly?: boolean;
  keywords?: string[];
  sort?: string;
  /** Full TraditionalSearchValue blob — lossless cross-device carrier. */
  filters?: Record<string, unknown>;
}

const DEBOUNCE_MS = 1200;

export function usePreferenceSync() {
  const { isAuthenticated } = useAuth();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<PreferencePatch>({});

  const sync = useCallback((patch: PreferencePatch) => {
    // Always write to visitor profile localStorage for anonymous seeding on sign-up
    if (typeof window !== 'undefined') {
      try {
        import('@/services/visitor-profile/visitorProfileService').then(({ writeDataPoints }) => {
          const points: Array<{ key: string; value: string | string[] | number | boolean; source: string }> = [];
          if (patch.regions?.length)       points.push({ key: 'deal_finder_regions',       value: patch.regions,       source: 'filter_sync' });
          if (patch.propertyTypes?.length) points.push({ key: 'deal_finder_property_types', value: patch.propertyTypes, source: 'filter_sync' });
          if (patch.priceMin  != null)     points.push({ key: 'deal_finder_price_min',      value: patch.priceMin,      source: 'filter_sync' });
          if (patch.priceMax  != null)     points.push({ key: 'deal_finder_price_max',      value: patch.priceMax,      source: 'filter_sync' });
          if (patch.bedsMin   != null)     points.push({ key: 'deal_finder_beds_min_num',   value: patch.bedsMin,       source: 'filter_sync' });
          if (points.length) writeDataPoints(points);
        }).catch(() => {});
      } catch { /* best-effort */ }
    }

    // For authenticated users: debounce DB write
    if (!isAuthenticated) return;

    Object.assign(pendingRef.current, patch);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const toSync = { ...pendingRef.current };
      pendingRef.current = {};
      if (Object.keys(toSync).length === 0) return;
      void secureFetch('/api/preferences/sync', {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(toSync),
      }).catch(() => {}); // best-effort, never throw
    }, DEBOUNCE_MS);
  }, [isAuthenticated]);

  return sync;
}
